/**
 * The company's job board colour, and the text colour that stays readable on it.
 *
 * A company may pick a pale brand colour, and white label text on pale pink
 * cannot be read. The label follows the background instead of assuming white.
 */

/** The product's own colour, used when a company has not picked one. */
export const DEFAULT_BRAND_COLOR = '#640d5f';

export const isHexColor = (value) => /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value || '');

/**
 * A pasted colour, tidied: "FF0000", "#ff0000 " and "ff0000" all become
 * "#ff0000". Anything that is not a colour comes back null.
 */
export function normaliseHex(value) {
  const cleaned = `#${String(value || '').trim().replace(/^#/, '').trim()}`.toLowerCase();
  return isHexColor(cleaned) ? cleaned : null;
}

/** #abc and #aabbcc both become {r, g, b}. */
function rgb(hex) {
  let value = (hex || '').replace('#', '');
  if (value.length === 3) value = value.split('').map((c) => c + c).join('');
  const int = parseInt(value, 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

/** Black or white, whichever can be read on this colour. */
export function readableTextOn(hex) {
  if (!isHexColor(hex)) return '#ffffff';
  const { r, g, b } = rgb(hex);
  // Rough perceived brightness — good enough to choose between two colours.
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 150 ? '#111827' : '#ffffff';
}

/** The gradient a primary button is filled with. */
export const brandGradient = (hex) => `linear-gradient(135deg, ${hex}, ${hex}cc)`;

/** Everything a primary button needs, in the company's colour. */
export function brandButtonStyle(hex) {
  const color = isHexColor(hex) ? hex : DEFAULT_BRAND_COLOR;
  return { background: brandGradient(color), color: readableTextOn(color) };
}
